import { FC, useEffect, useState } from 'react';
import { Center, InputRightElement } from '@chakra-ui/react';

import { InvoiceStatus } from '__generated__/GQL';
import errorMessages from 'common/errors/locales';
import {
  isFetchingMore,
  isLoading,
  isSettingVariables,
} from 'common/graphql/networkStatus';
import useDebounce from 'common/hooks/useDebounce';
import getErrorMessageIfPossible from 'common/hooks/useErrorHandledMutation/getErrorMessageIfPossible';
import { useGetInvoicesQuery } from 'common/hooks/useGetInvoicesQuery';
import Button from 'components/_core/Button';
import IconButton from 'components/_core/IconButton';
import SunshineLoader from 'components/_core/SunshineLoader';
import SunshineModal, {
  SunshineModalProps,
} from 'components/_core/SunshineModal';
import SearchBar from 'components/SearchBar';
import useToast from 'components/ToastProvider/useToast';

import { DetailedTransactionFragment } from '../../../../../../graphql/fragment/detailedTransaction.gql';
import useLinkInvoiceToTransactionMutation from '../../../../hooks/useLinkInvoiceToTransactionMutation';
import List from './components/List';
import locales from './locales';

interface InvoiceSearchModalProps extends Omit<SunshineModalProps, 'children'> {
  transaction: DetailedTransactionFragment;
}

const InvoiceSearchModal: FC<InvoiceSearchModalProps> = ({
  isOpen,
  onClose,
  transaction,
}) => {
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(
    null,
  );
  const [searchTerm, setSearchTerm] = useState<string>('');
  const debouncedSearchTerm = useDebounce(searchTerm.trim());

  const { showToast } = useToast();

  const [
    linkInvoiceToTransaction,
    {
      error: linkInvoiceToTransactionError,
      loading: linkInvoiceToTransactionLoading,
    },
  ] = useLinkInvoiceToTransactionMutation({
    transactionId: transaction.transactionId,
    transactionType: transaction.type,
  });

  const {
    error: getInvoicesError,
    fetchMore,
    invoices,
    networkStatus,
    pageInfo,
  } = useGetInvoicesQuery({
    filters: {
      searchInput: debouncedSearchTerm || undefined,
      status: [
        InvoiceStatus.MarkedAsPaid,
        InvoiceStatus.Overdue,
        InvoiceStatus.Paid,
        InvoiceStatus.Sent,
      ],
    },
    skip: !isOpen,
  });

  const loading = isLoading(networkStatus) || isSettingVariables(networkStatus);

  useEffect(() => {
    const error = getInvoicesError || linkInvoiceToTransactionError;

    if (error) {
      showToast({
        message:
          getErrorMessageIfPossible({ error }) ||
          errorMessages.localizedUnknownError,
        type: 'error',
      });
    }
  }, [getInvoicesError, linkInvoiceToTransactionError, showToast]);

  const clearSearch = () => setSearchTerm('');

  const handleClose = () => {
    clearSearch();
    setSelectedInvoiceId(null);
    onClose();
  };

  const handleSave = async () => {
    if (selectedInvoiceId) {
      await linkInvoiceToTransaction(selectedInvoiceId);
      handleClose();
    }
  };

  return (
    <SunshineModal
      contentProps={{ height: 'full' }}
      isOpen={isOpen}
      onClose={handleClose}
    >
      <SunshineModal.Header />

      <SunshineModal.Body
        css={{ paddingBottom: 0 }}
        display="flex"
        flexDirection="column"
      >
        <SunshineModal.Title>{locales.title}</SunshineModal.Title>

        <SearchBar
          marginBottom="space-16"
          onChange={setSearchTerm}
          placeholder={locales.searchPlaceholder}
          value={searchTerm}
        >
          {searchTerm ? (
            <InputRightElement>
              <IconButton
                aria-label={locales.searchClear}
                icon="cross"
                onClick={clearSearch}
                variant="inline-secondary"
              />
            </InputRightElement>
          ) : null}
        </SearchBar>

        {loading ? (
          <Center flexGrow={1}>
            <SunshineLoader />
          </Center>
        ) : (
          <List
            invoices={invoices}
            isFetchingMore={isFetchingMore(networkStatus)}
            onFetchMore={fetchMore}
            onSelectItem={setSelectedInvoiceId}
            pageInfo={pageInfo}
          />
        )}
      </SunshineModal.Body>

      <SunshineModal.Footer>
        <Button onClick={handleClose} variant="inline-secondary">
          {locales.cancel}
        </Button>

        <Button
          icon="arrow-right"
          isDisabled={!selectedInvoiceId}
          isLoading={linkInvoiceToTransactionLoading}
          onClick={handleSave}
        >
          {locales.submit}
        </Button>
      </SunshineModal.Footer>
    </SunshineModal>
  );
};

export default InvoiceSearchModal;
